import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api, API, fmtMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Pencil, Trash2, Download, Upload, Search, Sparkles, CheckSquare, Square, ChevronDown, ChevronRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import TransactionDialog from "../components/TransactionDialog";
import StatementPreviewDialog from "../components/StatementPreviewDialog";
import SpiderLoader from "../components/SpiderLoader";
import { getCategoryMeta } from "../lib/categoryIcons";
import { toast } from "sonner";

export default function Transactions() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Auto-open the add-transaction dialog when navigated via ?add=1 (mobile FAB)
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setEditing(null);
      setOpen(true);
      // Clear the query param so refreshing doesn't reopen it
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [importing, setImporting] = useState(false);
  const [statementBusy, setStatementBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewMeta, setPreviewMeta] = useState({ ocrUsed: false, period: null });
  const [ocrDpi, setOcrDpi] = useState(180); // 180 = standard, 300 = high-quality
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState("selected"); // 'selected' | 'all'
  const fileInputRef = useRef(null);
  const statementInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/transactions", { params: { limit: 5000 } });
      setList(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const cur = user?.currency || "USD";

  const filtered = list.filter(t => {
    if (typeFilter !== "all" && t.type !== typeFilter) return false;
    if (q && !(t.category.toLowerCase().includes(q.toLowerCase()) || (t.description || "").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  // Group filtered transactions by month (YYYY-MM). Preserves the newest-first
  // ordering since `list` is already sorted DESC by date.
  const monthGroups = useMemo(() => {
    const map = new Map();
    filtered.forEach(t => {
      let key = "unknown";
      let label = "Unknown date";
      try {
        const d = new Date(t.date);
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        label = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
      } catch { /* keep defaults */ }
      if (!map.has(key)) map.set(key, { key, label, items: [] });
      map.get(key).items.push(t);
    });
    return Array.from(map.values()).map(g => {
      const income = g.items.filter(t => t.type === "income").reduce((s, t) => s + t.amount, 0);
      const expense = g.items.filter(t => t.type === "expense").reduce((s, t) => s + t.amount, 0);
      return { ...g, income, expense, net: income - expense, count: g.items.length };
    });
  }, [filtered]);

  const [collapsedMonths, setCollapsedMonths] = useState({});
  const toggleMonth = (key) => setCollapsedMonths(c => ({ ...c, [key]: !c[key] }));
  const toggleMonthSelection = (group) => {
    const groupIds = group.items.map(t => t.id);
    const allSelected = groupIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      groupIds.forEach(id => { allSelected ? next.delete(id) : next.add(id); });
      return next;
    });
  };

  const remove = async (id) => {
    try {
      const r = await api.delete(`/transactions/${id}`);
      const restored = r.data?.restored_debts || [];
      if (restored.length > 0) {
        const r0 = restored[0];
        toast.success(`Deleted · restored ${fmtMoney(r0.restored_amount, cur)} to ${r0.debt_name}`);
      } else {
        toast.success("Deleted");
      }
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllFiltered = () => {
    const allIds = filtered.map(t => t.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const openConfirm = (mode) => { setConfirmMode(mode); setConfirmOpen(true); };
  const runBulkDelete = async () => {
    setConfirmOpen(false);
    // Snapshot the rows we're about to delete so the user can undo
    const deletingIds = confirmMode === "all"
      ? list.map(t => t.id)
      : Array.from(selectedIds);
    const snapshot = list.filter(t => deletingIds.includes(t.id));
    try {
      let r;
      if (confirmMode === "all") {
        r = await api.post("/transactions/bulk-delete", { all: true });
      } else {
        r = await api.post("/transactions/bulk-delete", { ids: deletingIds });
      }
      const count = r.data.deleted;
      const restored = r.data?.restored_debts || [];
      clearSelection();
      load();
      const suffix = restored.length > 0
        ? ` · restored ${fmtMoney(restored.reduce((s, x) => s + (x.restored_amount || 0), 0), cur)} to ${restored.length} debt${restored.length !== 1 ? "s" : ""}`
        : "";
      toast.success(`Deleted ${count} transaction${count !== 1 ? "s" : ""}${suffix}`, {
        duration: 8000,
        action: snapshot.length > 0 ? {
          label: "Undo",
          onClick: async () => {
            try {
              const payload = {
                transactions: snapshot.map(t => ({
                  type: t.type,
                  amount: t.amount,
                  category: t.category,
                  description: t.description || "",
                  date: t.date,
                  source: t.source || "manual",
                })),
              };
              const rr = await api.post("/transactions/bulk-create", payload);
              toast.success(`Restored ${rr.data.created} transaction${rr.data.created !== 1 ? "s" : ""}`);
              load();
            } catch { toast.error("Couldn't restore — please try again"); }
          },
        } : undefined,
      });
    } catch { toast.error("Delete failed"); }
  };

  const exportCsv = () => {
    // Use fetch to include credentials and trigger download
    fetch(`${API}/transactions/export`, { credentials: "include" })
      .then(r => r.blob())
      .then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "transactions.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const handleImportClick = () => fileInputRef.current?.click();
  const handleStatementClick = () => statementInputRef.current?.click();

  const handleStatementChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!(name.endsWith(".pdf") || name.endsWith(".csv") || name.endsWith(".txt"))) {
      toast.error("Upload a PDF, CSV or TXT bank statement");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File is too large (max 8 MB)");
      return;
    }
    setStatementBusy(true);
    toast("Reading your statement… this can take up to 30 seconds");
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await fetch(`${API}/transactions/import-statement?ocr_dpi=${ocrDpi}`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Statement import failed");
      } else if ((data.extracted ?? 0) > 0) {
        setPreviewRows(data.transactions || []);
        setPreviewMeta({ ocrUsed: data.ocr_used, period: data.period_detected });
        setPreviewOpen(true);
        if (data.ocr_used) {
          toast("Statement OCR'd via vision — verify the amounts below");
        }
      } else {
        const firstErr = data.errors?.[0]?.error;
        toast(firstErr || "No transactions were extracted from that file");
      }
    } catch (err) {
      toast.error("Couldn't process the statement — please try again");
    } finally {
      setStatementBusy(false);
    }
  };

  const confirmStatement = async (selectedRows) => {
    setSaving(true);
    try {
      const r = await api.post("/transactions/bulk-create", { transactions: selectedRows });
      const linked = r.data.linked_bills || [];
      const suggested = r.data.suggested_bills || [];
      // Base toast
      const linkedNote = linked.length > 0
        ? ` · ${linked.length} auto-linked to bill${linked.length !== 1 ? "s" : ""}`
        : "";
      toast.success(`Saved ${r.data.created} transaction${r.data.created !== 1 ? "s" : ""}${linkedNote}`);

      // Suggest new bills — one toast per unique merchant with a "Create bill" action
      for (const s of suggested) {
        toast(`We noticed a ${s.suggested_name} charge — create a bill?`, {
          duration: 15000,
          action: {
            label: "Create bill",
            onClick: async () => {
              try {
                const bill = {
                  name: (s.suggested_name || "").slice(0, 60).replace(/\s+/g, " ").trim(),
                  category: s.category,
                  amount: s.amount,
                  // Default due day to today so it fires an immediate reminder if the user forgets to edit
                  due_day_of_month: new Date().getDate(),
                  notes: "Auto-suggested from statement import",
                };
                await api.post("/bills", bill);
                toast.success(`Bill created — ${bill.name}. Edit due day on /bills`);
              } catch {
                toast.error("Couldn't create bill");
              }
            },
          },
        });
      }
      setPreviewOpen(false);
      setPreviewRows([]);
      load();
    } catch {
      toast.error("Couldn't save — please try again");
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-selected
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please pick a .csv file");
      return;
    }
    setImporting(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await fetch(`${API}/transactions/import`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Import failed");
      } else if (data.created > 0 && data.skipped > 0) {
        toast.success(`Imported ${data.created} · Skipped ${data.skipped}`);
      } else if (data.created > 0) {
        toast.success(`Imported ${data.created} transaction${data.created > 1 ? "s" : ""}`);
      } else {
        toast(`No new transactions were imported${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
      }
      if (data.errors?.length) {
        console.warn("CSV import errors:", data.errors);
      }
      load();
    } catch (err) {
      toast.error("Import failed — please try again");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Shell>
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">Transactions</h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">All your money moves in one place</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="import-csv-input"
            onChange={handleFileChange}
          />
          <input
            ref={statementInputRef}
            type="file"
            accept=".pdf,.csv,.txt,application/pdf,text/csv,text/plain"
            className="hidden"
            data-testid="import-statement-input"
            onChange={handleStatementChange}
          />
          <div className="inline-flex h-9 sm:h-10 rounded-full bg-spidey-red text-white overflow-hidden">
            <button
              type="button"
              onClick={handleStatementClick}
              disabled={statementBusy}
              className="flex items-center gap-1 px-3 sm:px-4 h-full text-xs sm:text-sm font-semibold hover:opacity-90 disabled:opacity-70 transition-opacity"
              data-testid="import-statement-btn"
            >
              <Sparkles className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{statementBusy ? "Reading…" : "Import statement"}</span>
              <span className="sm:hidden">{statementBusy ? "…" : "Statement"}</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={statementBusy}
                  className="h-full border-l border-white/25 px-2 flex items-center hover:opacity-90 disabled:opacity-70 transition-opacity"
                  data-testid="import-statement-options-btn"
                  aria-label="Import statement options"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="import-statement-options-menu">
                <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-slate-500">
                  OCR quality (for scanned PDFs only)
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setOcrDpi(180)}
                  data-testid="ocr-dpi-standard"
                  className={ocrDpi === 180 ? "bg-slate-100 dark:bg-slate-800 font-semibold" : ""}
                >
                  <div className="flex-1">
                    <div className="text-sm">Standard <span className="text-xs text-slate-500">(180 DPI, faster)</span></div>
                    <div className="text-[11px] text-slate-500">Fine for most scans</div>
                  </div>
                  {ocrDpi === 180 && <Check className="w-4 h-4 spidey-red" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setOcrDpi(240)}
                  data-testid="ocr-dpi-high"
                  className={ocrDpi === 240 ? "bg-slate-100 dark:bg-slate-800 font-semibold" : ""}
                >
                  <div className="flex-1">
                    <div className="text-sm">High <span className="text-xs text-slate-500">(240 DPI)</span></div>
                    <div className="text-[11px] text-slate-500">Better for small print</div>
                  </div>
                  {ocrDpi === 240 && <Check className="w-4 h-4 spidey-red" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setOcrDpi(300)}
                  data-testid="ocr-dpi-ultra"
                  className={ocrDpi === 300 ? "bg-slate-100 dark:bg-slate-800 font-semibold" : ""}
                >
                  <div className="flex-1">
                    <div className="text-sm">Ultra <span className="text-xs text-slate-500">(300 DPI, slowest)</span></div>
                    <div className="text-[11px] text-slate-500">Blurry / low-quality scans</div>
                  </div>
                  {ocrDpi === 300 && <Check className="w-4 h-4 spidey-red" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            variant="outline"
            onClick={handleImportClick}
            disabled={importing}
            className="rounded-full h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm"
            data-testid="import-csv-btn"
          >
            <Upload className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{importing ? "Importing…" : "Import CSV"}</span><span className="sm:hidden">CSV</span>
          </Button>
          <Button variant="outline" onClick={exportCsv} className="rounded-full h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm" data-testid="export-csv-btn">
            <Download className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Export</span>
          </Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-spidey-red text-white hover:opacity-90 h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm" data-testid="add-tx-btn">
            <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Add</span>
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="tx-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search category or note…" className="pl-9 rounded-full" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[140px] rounded-full" data-testid="tx-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="expense">Expenses</SelectItem>
              <SelectItem value="income">Income</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bulk action bar */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between py-2 mb-2 -mx-2 px-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            <button
              onClick={selectAllFiltered}
              data-testid="select-all-btn"
              className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:spidey-red"
            >
              {filtered.length > 0 && filtered.every(t => selectedIds.has(t.id))
                ? <CheckSquare className="w-4 h-4 spidey-red" />
                : <Square className="w-4 h-4" />
              }
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select all"}
            </button>
            <div className="flex items-center gap-2">
              {selectedIds.size > 0 && (
                <>
                  <button
                    onClick={clearSelection}
                    data-testid="clear-selection-btn"
                    className="text-xs font-semibold text-slate-500 dark:text-slate-400 hover:underline"
                  >Clear</button>
                  <Button
                    onClick={() => openConfirm("selected")}
                    data-testid="delete-selected-btn"
                    className="rounded-full bg-rose-600 text-white hover:bg-rose-700 h-8 px-4 text-xs"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete {selectedIds.size}
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={() => openConfirm("all")}
                data-testid="delete-all-btn"
                className="rounded-full h-8 px-4 text-xs text-rose-600 border-rose-200 dark:border-rose-800/60 hover:bg-rose-50 dark:hover:bg-rose-900/30"
              >
                Delete all
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-16 flex flex-col items-center justify-center" data-testid="tx-loading">
            <span className="spidey-red"><SpiderLoader size={48} label="Swinging in your transactions…" /></span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <div className="text-slate-500 dark:text-slate-400 text-sm">No transactions found.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {monthGroups.map(g => {
              const isCollapsed = !!collapsedMonths[g.key];
              const groupIds = g.items.map(t => t.id);
              const allSelected = groupIds.length > 0 && groupIds.every(id => selectedIds.has(id));
              const someSelected = !allSelected && groupIds.some(id => selectedIds.has(id));
              return (
                <div key={g.key} data-testid={`tx-month-${g.key}`}>
                  {/* Sticky month header */}
                  <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2.5 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-y border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={() => toggleMonthSelection(g)}
                        data-testid={`select-month-${g.key}`}
                        className={someSelected ? "data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-600" : ""}
                        aria-label={`Select all transactions in ${g.label}`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleMonth(g.key)}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                        data-testid={`toggle-month-${g.key}`}
                      >
                        {isCollapsed
                          ? <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                          : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                        }
                        <div className="min-w-0 flex-1">
                          <div className="font-display text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100 truncate">
                            {g.label}
                            <span className="ml-2 text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              {g.count} {g.count === 1 ? "item" : "items"}
                            </span>
                          </div>
                          <div className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex gap-3 tabular">
                            {g.income > 0 && <span className="text-emerald-600 font-semibold">+{fmtMoney(g.income, cur)}</span>}
                            {g.expense > 0 && <span className="text-rose-600 font-semibold">-{fmtMoney(g.expense, cur)}</span>}
                          </div>
                        </div>
                      </button>
                      <div className={`tabular text-sm sm:text-base font-bold shrink-0 ${g.net >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`month-net-${g.key}`}>
                        {g.net >= 0 ? "+" : "-"}{fmtMoney(Math.abs(g.net), cur)}
                      </div>
                    </div>
                  </div>

                  {/* Rows for the month */}
                  {!isCollapsed && (
                    <div className="mt-1">
                      {g.items.map(t => {
                        const meta = getCategoryMeta(t.category);
                        const CatIcon = meta.Icon;
                        return (
                        <div key={t.id} data-testid={`tx-row-${t.id}`} className={`flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-800/50 -mx-2 px-2 rounded-xl transition-colors ${selectedIds.has(t.id) ? "bg-rose-50/60 dark:bg-rose-900/20" : ""}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <Checkbox
                              checked={selectedIds.has(t.id)}
                              onCheckedChange={() => toggleRow(t.id)}
                              data-testid={`select-tx-${t.id}`}
                            />
                            <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${meta.bg} ${meta.fg}`}>
                              <CatIcon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{t.category}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{t.description || "—"} · {new Date(t.date).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className={`text-sm font-semibold tabular ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                              {t.type === "income" ? "+" : "-"}{fmtMoney(t.amount, cur)}
                            </div>
                            <button data-testid={`edit-tx-${t.id}`} onClick={() => { setEditing(t); setOpen(true); }} className="w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-800 grid place-items-center text-slate-500 dark:text-slate-400">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button data-testid={`delete-tx-${t.id}`} onClick={() => remove(t.id)} className="w-8 h-8 rounded-full hover:bg-rose-50 grid place-items-center text-rose-500">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TransactionDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
      <StatementPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        extracted={previewRows}
        currency={cur}
        onConfirm={confirmStatement}
        saving={saving}
        meta={previewMeta}
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="bulk-delete-confirm" className="rounded-3xl bg-white dark:bg-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100">
              {confirmMode === "all" ? "Delete all transactions?" : `Delete ${selectedIds.size} transaction${selectedIds.size !== 1 ? "s" : ""}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-500 dark:text-slate-400">
              This action can't be undone. {confirmMode === "all" ? "Every transaction in your account will be removed." : "The selected transactions will be permanently removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="bulk-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulkDelete}
              data-testid="bulk-confirm-btn"
              className="rounded-full bg-rose-600 text-white hover:bg-rose-700"
            >
              {confirmMode === "all" ? "Delete everything" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
